import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import { Inject } from 'inversify-props';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import { AdminUserModel } from '@/models/DTO/admin/admin-user.model';
import { AdminUserService } from '@/services/admin-user.service';
import { AdminUserActions } from '@/constants/actions.constants';
import { AdminUserMutations } from '@/constants/mutation.constants';
import { AdminUserGetters } from '@/constants/getters.constants';

export const ADMIN_USER_MODULE_NAME = 'adminUser';

class DefaultState {
  public adminUsers: AdminUserModel[] = [];
  public currentAdminUser: AdminUserModel | null = null;
  public errorResponse: BaseAPIErrorModel | null = null;
  public showLoading = false;
  public showTableLoading = false;
  public lockOrUnlockAdminUserSuccess: boolean | null = null;
}

const defaultState = new DefaultState();

@Module({
  namespaced: true,
  name: ADMIN_USER_MODULE_NAME
})
export default class AdminUserModule extends VuexModule {
  // #region Services
  @Inject()
  private readonly _adminUserService!: AdminUserService;
  // #endregion

  // #region State
  public adminUsers: AdminUserModel[] = defaultState.adminUsers;
  public currentAdminUser: AdminUserModel | null =
    defaultState.currentAdminUser;
  public showLoading = defaultState.showLoading;
  public showTableLoading = defaultState.showTableLoading;
  public errorResponse: BaseAPIErrorModel | null = defaultState.errorResponse;
  public lockOrUnlockAdminUserSuccess: boolean | null =
    defaultState.lockOrUnlockAdminUserSuccess;
  // #endregion

  // #region Getters
  public get [AdminUserGetters.hasErrorMessages](): boolean {
    return this.context.getters[AdminUserGetters.errorMessages].length > 0;
  }

  public get [AdminUserGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }
  // #endregion

  // #region Actions
  @Action
  public async [AdminUserActions.getAllAdminUsersAction](): Promise<void> {
    try {
      this.context.commit(AdminUserMutations.showTableLoadingMutation, true);

      // remove any errors
      this.context.commit(AdminUserMutations.errorMutation, null);

      const resp = await this._adminUserService.getAllAdminUsers();

      // success
      this.context.commit(AdminUserMutations.setAllAdminUserssMutation, [
        ...resp
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AdminUserMutations.errorMutation, e);
    } finally {
      this.context.commit(AdminUserMutations.showTableLoadingMutation, false);
    }
  }

  @Action
  public async [AdminUserActions.getAdminUserByIdAction](
    id: string
  ): Promise<void> {
    try {
      // Not needed for this operation
      // this.context.commit(AdminUserMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(AdminUserMutations.errorMutation, null);

      const resp = await this._adminUserService.getAdminUserById(id);

      // success
      this.context.commit(AdminUserMutations.setCurrentAdminUserMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AdminUserMutations.errorMutation, e);
    } finally {
      this.context.commit(AdminUserMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [AdminUserActions.resetStateAction](): Promise<void> {
    this.context.commit(AdminUserMutations.resetStateMutation);
  }

  @Action
  public async [AdminUserActions.lockAdminUserAction](
    id: string
  ): Promise<void> {
    try {
      // Not needed for this operation
      // this.context.commit(AdminUserMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(AdminUserMutations.errorMutation, null);
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        null
      );

      await this._adminUserService.lockAdminUser(id);

      // success
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        true
      );
    } catch (error) {
      // fail
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        false
      );

      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AdminUserMutations.errorMutation, e);
    } finally {
      this.context.commit(AdminUserMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [AdminUserActions.unlockAdminUserAction](
    id: string
  ): Promise<void> {
    try {
      // Not needed for this operation
      // this.context.commit(AdminUserMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(AdminUserMutations.errorMutation, null);
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        null
      );

      await this._adminUserService.unlockAdminUser(id);

      // success
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        true
      );
    } catch (error) {
      // fail
      this.context.commit(
        AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation,
        false
      );

      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(AdminUserMutations.errorMutation, e);
    } finally {
      this.context.commit(AdminUserMutations.showLoadingMutation, false);
    }
  }
  // #endregion

  // #region Mutations
  @Mutation
  private [AdminUserMutations.setAllAdminUserssMutation](
    data: AdminUserModel[]
  ) {
    this.adminUsers = [...data];
  }

  @Mutation
  private [AdminUserMutations.setCurrentAdminUserMutation](
    data: AdminUserModel
  ) {
    this.currentAdminUser = { ...data };
  }

  @Mutation
  private [AdminUserMutations.showLoadingMutation](show: boolean) {
    this.showLoading = show;
  }

  @Mutation
  private [AdminUserMutations.showTableLoadingMutation](show: boolean) {
    this.showTableLoading = show;
  }

  @Mutation
  private [AdminUserMutations.errorMutation](
    errorModel: BaseAPIErrorModel | null
  ) {
    this.errorResponse = errorModel;
  }

  @Mutation
  private [AdminUserMutations.resetStateMutation]() {
    Object.assign(this, defaultState);
  }

  @Mutation
  private [AdminUserMutations.setLockOrUnlockAdminUserSuccessMutation](
    data: boolean | null
  ) {
    this.lockOrUnlockAdminUserSuccess = data;
  }
  // #endregion
}
